import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PublicNav, Footer, waLink, WhatsAppIcon } from "@/pages/Landing";
import { useSEO } from "@/pages/site/shared";

const CATS = [
  { id: "", label: "All" },
  { id: "extinguishers", label: "Extinguishers" },
  { id: "alarms", label: "Alarms & Detection" },
  { id: "suppression", label: "Suppression" },
  { id: "hydrants", label: "Hydrants & Hose" },
  { id: "safety", label: "Safety & Signage" },
];

const IMG = {
  extinguishers: "https://images.unsplash.com/photo-1625958936686-a9343dc35b5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  alarms: "https://images.unsplash.com/photo-1666734057863-0087ebfa44a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  suppression: "https://images.unsplash.com/photo-1772034736198-b194e4d72a1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  hydrants: "https://images.unsplash.com/photo-1650621031631-d4772e6a3182?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  safety: "https://images.unsplash.com/photo-1559656428-6f31733e0937?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

export default function Catalog() {
  useSEO(
    "Fire Safety Products Catalog | SecureFlame Fire Systems",
    "Browse SecureFlame's fire safety product catalog — extinguishers, alarms & detection, suppression, hydrants & hose, and safety & signage equipment. Request a quotation with installation included."
  );
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/products", { params: cat ? { category: cat } : {} }).then((r) => setProducts(r.data));
  }, [cat]);

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicNav />
      <section className="bg-secondary text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Catalog</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">Fire Safety Products</h1>
          <p className="mt-3 text-slate-300 max-w-lg">Certified equipment for every environment. Request a quotation and we'll price it with installation & transportation included.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12 w-full flex-1">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-10">
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`cat-${c.id || "all"}`}
                className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${cat === c.id ? "bg-primary text-white border-primary" : "bg-card border-border text-secondary hover:border-primary"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="pl-9 rounded-sm" data-testid="product-search" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <div key={p.id} className="group bg-card border border-border rounded-sm overflow-hidden hover:-translate-y-1 transition-transform duration-200 animate-fade-up" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`product-card-${p.id}`}>
              <div className="h-44 bg-muted overflow-hidden">
                <img src={p.image_url || IMG[p.category] || IMG.extinguishers} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="p-5">
                <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider mb-2">{p.category}</Badge>
                <h3 className="font-heading font-bold text-base text-secondary leading-tight">{p.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider">Price on request</span>
                  <div className="flex items-center gap-2">
                    <a href={waLink(`Hi Secureflame, I'm interested in "${p.name}". Please share a quotation.`)} target="_blank" rel="noreferrer" aria-label={`Enquire about ${p.name} on WhatsApp`} data-testid={`whatsapp-${p.id}`}>
                      <Button size="sm" className="rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white px-2.5"><WhatsAppIcon size={16} /></Button>
                    </a>
                    <Link to="/register"><Button size="sm" variant="outline" className="rounded-sm" data-testid={`quote-${p.id}`}>Get Quote</Button></Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-24 text-muted-foreground"><Package className="mx-auto mb-3" /> No products found.</div>
        )}

        <div className="mt-14 text-center">
          <p className="text-muted-foreground mb-4">Ready for a tailored quotation?</p>
          <Link to="/register"><Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="catalog-register-btn">Register to Request a Quote</Button></Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
